import { DealTransformed } from "@/lib/schema/deal-schema";
import { getStageText, resolveIndustryLabel } from "@/lib/utils";
import { getLastActivityDate } from "@/services/deals-service";
import { getFitScore, getGapAnalysisScore } from "./utils";

/**
 * The single description of what a deal column *is*.
 *
 * Columns, sorting and filtering were three hardcoded lists that had to be kept
 * in step by hand — `SortField` was a six-literal union in `deals-view-store`,
 * `FilterValues` was a fixed interface in `filter-sheet`, and the table itself
 * had ten hand-written `<TableHead>` blocks. Adding a column meant editing four
 * files, and adding a *user-defined* column was impossible. Saved views and AI
 * columns both need to name a field and have every part of the UI agree on what
 * that means, so the description has to live in one place.
 *
 * Rendering deliberately stays in `table-view.tsx`. Those cells carry links,
 * loading states, inline status dropdowns and per-cell tooltips that a generic
 * renderer would flatten; this registry owns identity, not markup.
 *
 * `id` is a persisted contract. Once a view is saved it stores field ids, so
 * renaming one silently empties somebody's saved view — add a new field and
 * migrate instead.
 */

export type FieldType =
  | "text"
  | "number"
  | "currency"
  | "date"
  | "select"
  | "multiselect"
  | "boolean";

/**
 * Where the value comes from, which decides how the server can filter it.
 *
 * `company` fields live on the Company collection, so a server-side filter has
 * to resolve matching company ids first and then filter applications — the same
 * two-step the raise board already does in `raise_board.ts`. `computed` fields
 * are derived client-side and cannot be filtered server-side at all without
 * being denormalised first.
 */
export type FieldSource = "company" | "application" | "computed" | "ai";

export interface FieldDef {
  id: string;
  label: string;
  type: FieldType;
  source: FieldSource;
  /** Value used for sorting, filtering and tests. Not for display. */
  accessor: (deal: DealTransformed) => unknown;
  sortable: boolean;
  filterable: boolean;
  /**
   * Whether the database can order by this field.
   *
   * False for anything `computed`: fit score, gap analysis and traffic are
   * assembled after the query from AI generations and website analytics, so
   * there is no column to sort on. They stay sortable in the client, and the
   * table falls back to loading unpaginated when one of them is the active
   * sort — sorting a single page by a value the server did not order would
   * silently mean "sort these 25", which is worse than not offering it.
   *
   * `lastActivity` is the exception among computed fields: it was denormalised
   * onto the deal precisely so the default sort could survive paging.
   *
   * Also false for company-backed fields (name, location). Paging is applied
   * before the `$lookup` that joins the company — which is what makes a large
   * workspace affordable — so there is no company_name to order on at that
   * point. Sorting by name therefore loads unpaginated too.
   */
  serverSortable?: boolean;
  /** Visible when a view does not specify its own column set. */
  defaultVisible: boolean;
  /**
   * Tailwind responsive hiding, preserved from the current table so narrow
   * screens keep the same column set they have today.
   */
  responsiveClass?: string;
  /** Column cannot be hidden — without it a row has nothing to identify it. */
  locked?: boolean;
  description?: string;
}

/** Empty and "—" placeholders both mean "no industry", per the current cell. */
const industryLabels = (deal: DealTransformed): string[] =>
  Array.from(
    new Set(
      (deal.overview.industry ?? [])
        .map(resolveIndustryLabel)
        .filter((label) => label && label !== "—")
    )
  );

const hasPitchDeck = (deal: DealTransformed): boolean =>
  (deal.company_files ?? []).some(
    (file) => file.file_type === "company pitchdeck"
  );

export const DEAL_FIELDS: FieldDef[] = [
  {
    id: "name",
    label: "Deal Name",
    type: "text",
    source: "company",
    accessor: (d) => d.overview.name ?? "",
    sortable: true,
    filterable: true,
    defaultVisible: true,
    locked: true,
    description: "The name of the company or investment opportunity",
  },
  {
    id: "fitScore",
    label: "Fit Score",
    type: "number",
    source: "computed",
    accessor: (d) => getFitScore(d),
    sortable: true,
    filterable: true,
    defaultVisible: true,
    description:
      "A score indicating how well the deal matches your investment criteria",
  },
  {
    id: "industry",
    label: "Industry",
    type: "multiselect",
    source: "company",
    accessor: industryLabels,
    sortable: false,
    filterable: true,
    defaultVisible: true,
    responsiveClass: "hidden lg:table-cell",
    description: "The business sectors or industries the company operates in",
  },
  {
    id: "traffic",
    label: "Traffic",
    type: "number",
    source: "computed",
    accessor: (d) => d.websiteTrafficGrowth ?? null,
    sortable: false,
    filterable: false,
    defaultVisible: true,
    responsiveClass: "hidden xl:table-cell",
    description:
      "Month-on-month change in estimated website visits. Third-party estimates, not the company's own analytics.",
  },
  {
    id: "source",
    label: "Source",
    type: "select",
    source: "application",
    accessor: (d) => d.overview.source || null,
    sortable: false,
    filterable: true,
    defaultVisible: true,
    description: "How this deal was sourced",
  },
  {
    id: "status",
    serverSortable: true,
    label: "Status",
    type: "select",
    source: "application",
    accessor: (d) => d.overview.status,
    sortable: true,
    filterable: true,
    defaultVisible: true,
    locked: true,
    description: "Current stage of the deal in your pipeline",
  },
  {
    id: "lastActivity",
    serverSortable: true,
    label: "Last Activity",
    type: "date",
    source: "computed",
    accessor: (d) => getLastActivityDate(d),
    sortable: true,
    filterable: false,
    defaultVisible: true,
    responsiveClass: "hidden lg:table-cell",
    description: "Time since the most recent update or activity on this deal",
  },

  // Below here: fields the data already carries that the table has never shown.
  // Hidden by default so the table looks exactly as it does today, but
  // available in the columns menu and to filters and saved views.

  {
    id: "added",
    serverSortable: true,
    label: "Added",
    type: "date",
    source: "application",
    accessor: (d) => new Date(d.overview.addedOn),
    sortable: true,
    filterable: true,
    defaultVisible: false,
    description: "When this deal was added to the workspace",
  },
  {
    id: "gapAnalysis",
    label: "Gap Analysis",
    type: "number",
    source: "computed",
    accessor: (d) => getGapAnalysisScore(d),
    sortable: true,
    filterable: true,
    defaultVisible: false,
    description:
      "Completeness of the information held on this deal. Deliberately hidden by default — too dense for daily scanning.",
  },
  {
    id: "companyStage",
    serverSortable: true,
    label: "Stage",
    type: "select",
    source: "company",
    accessor: (d) => getStageText(d.overview.companyStage),
    sortable: true,
    filterable: true,
    defaultVisible: false,
    description: "The company's funding stage",
  },
  {
    id: "location",
    label: "Location",
    type: "text",
    source: "company",
    accessor: (d) => d.overview.location ?? null,
    sortable: true,
    filterable: true,
    defaultVisible: false,
    description: "Where the company is based",
  },
  {
    id: "country",
    serverSortable: true,
    label: "Country",
    type: "select",
    source: "company",
    accessor: (d) => d.overview.country ?? null,
    sortable: true,
    filterable: true,
    defaultVisible: false,
  },
  {
    id: "website",
    label: "Website",
    type: "text",
    source: "company",
    accessor: (d) => d.overview.website || null,
    sortable: false,
    filterable: false,
    defaultVisible: false,
  },
  {
    id: "businessModel",
    serverSortable: true,
    label: "Business Model",
    type: "select",
    source: "company",
    accessor: (d) => d.overview.businessModel ?? null,
    sortable: true,
    filterable: true,
    defaultVisible: false,
  },
  {
    id: "hasPitchDeck",
    label: "Pitch Deck",
    type: "boolean",
    source: "application",
    accessor: hasPitchDeck,
    sortable: false,
    filterable: true,
    defaultVisible: false,
    description: "Whether a pitch deck has been uploaded",
  },
];

const BY_ID = new Map(DEAL_FIELDS.map((field) => [field.id, field]));

export const getField = (id: string): FieldDef | undefined => BY_ID.get(id);

export const defaultVisibleFieldIds = (): string[] =>
  DEAL_FIELDS.filter((f) => f.defaultVisible).map((f) => f.id);

export const sortableFields = (): FieldDef[] =>
  DEAL_FIELDS.filter((f) => f.sortable);

/**
 * Whether the server can order by this field, and therefore whether the table
 * may page while sorted by it.
 */
export const isServerSortable = (id: string): boolean =>
  BY_ID.get(id)?.serverSortable === true;

export const filterableFields = (): FieldDef[] =>
  DEAL_FIELDS.filter((f) => f.filterable);

/**
 * Resolve a saved column list against the registry.
 *
 * Saved views hold field ids, so a view written before a field was renamed or
 * removed will name ids that no longer exist. Dropping them silently is right —
 * the alternative is a crash or a blank column — but locked fields are always
 * restored, so a stale view can never render rows with nothing to identify them.
 */
export const resolveVisibleFields = (
  columnIds: string[] | undefined
): FieldDef[] => {
  if (!columnIds || columnIds.length === 0) {
    return DEAL_FIELDS.filter((f) => f.defaultVisible);
  }

  const resolved = columnIds
    .map((id) => BY_ID.get(id))
    .filter((f): f is FieldDef => !!f);

  const missingLocked = DEAL_FIELDS.filter(
    (f) => f.locked && !resolved.some((r) => r.id === f.id)
  );

  // Locked fields go first so an identifying column never lands mid-row.
  return [...missingLocked, ...resolved];
};
